"use client";

import type { ReadingStorySpec } from "@/lib/whiteboard/spec";
import React, { useState } from "react";
import { LessonButton } from "./LessonPrimitives";
import { LessonHeading, useLessonPresentation } from "./LessonPrimitives";
import type { WhiteboardCardEventHandler } from "./types";

interface ReadingStoryCardProps {
  spec: ReadingStorySpec;
  onEvent?: WhiteboardCardEventHandler;
  showContent?: boolean;
}

/**
 * A reading surface with one fixed completion signal. Questions and teaching
 * prompts stay in the tutor's voice; the board contains no generated exercise UI.
 */
export const ReadingStoryCard: React.FC<ReadingStoryCardProps> = ({
  spec,
  onEvent,
  showContent = true,
}) => {
  const [ready, setReady] = useState(false);
  const lesson = useLessonPresentation();
  const paragraphs = spec.story
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

  return (
    <article aria-label={spec.title || "Reading story"} className="space-y-5 font-sans">
      {showContent && spec.title && (
        <LessonHeading
          data-whiteboard-lesson-title
          className="text-xl font-semibold leading-snug text-[#F9F9F9] sm:text-2xl"
        >
          {spec.title}
        </LessonHeading>
      )}
      {showContent && (
        <div
          data-whiteboard-reading-text
          className={
            lesson
              ? "space-y-4 text-base leading-relaxed text-[#F9F9F9]"
              : "space-y-4 text-[1.05rem] leading-8 text-[#F9F9F9] sm:text-lg sm:leading-9"
          }
        >
          {paragraphs.map((paragraph, index) => (
            <p key={`${index}:${paragraph.slice(0, 24)}`} className="whitespace-pre-wrap">
              {paragraph}
            </p>
          ))}
        </div>
      )}
      <div className="flex justify-end border-t border-slate-900/15 pt-4">
        <LessonButton
          state={ready ? "correct" : "neutral"}
          data-whiteboard-reading-complete
          type="button"
          disabled={ready}
          onClick={() => {
            if (ready) return;
            setReady(true);
            onEvent?.({
              event: "reading_complete",
              action: "finished_reading",
              target: spec.title || "story",
              progress: "Learner is ready to discuss the story",
              resolved: false,
            });
          }}
          className="rounded-xl border border-slate-900/20 bg-white/80 px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm transition-colors hover:bg-white focus:outline-none focus:ring-2 focus:ring-[#FD954F]/55 disabled:cursor-default disabled:border-emerald-700/20 disabled:bg-emerald-50/80 disabled:text-emerald-800"
        >
          {ready ? "Ready to discuss" : "Done reading"}
        </LessonButton>
      </div>
    </article>
  );
};
